import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';
import { toast } from 'sonner';
import { Package, MapPin, Phone, Mail, User, Building2, ArrowLeft, MessageSquare, Tag, Calendar, Box, Share2, Copy, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MaterialDetails = () => {
  const { materialId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [material, setMaterial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = `${window.location.origin}/materials/${materialId}`;

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link skopiowany!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Nie udało się skopiować linku');
    }
  };

  useEffect(() => {
    const fetchMaterial = async () => {
      try {
        const res = await fetch(`${API}/materials/${materialId}`);
        if (res.ok) {
          setMaterial(await res.json());
        } else {
          toast.error('Nie znaleziono ogłoszenia');
          navigate('/materials');
        }
      } catch (err) {
        console.error('Error:', err);
        toast.error('Błąd podczas ładowania');
      } finally {
        setLoading(false);
      }
    };
    fetchMaterial();
  }, [materialId, navigate]);

  const handleSendMessage = async () => {
    if (!user) {
      toast.error('Zaloguj się, aby wysłać wiadomość');
      navigate('/login');
      return;
    }
    
    if (!message.trim()) {
      toast.error('Wpisz wiadomość');
      return;
    }

    setSending(true);
    try {
      const res = await fetch(`${API}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          receiver_id: material.user_id,
          content: `📦 Zapytanie o: ${material.title}\n\n${message}`
        })
      });

      if (res.ok) {
        toast.success('Wiadomość wysłana!');
        setMessage('');
      } else {
        toast.error('Błąd podczas wysyłania');
      }
    } catch (err) {
      toast.error('Błąd połączenia');
    } finally {
      setSending(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!material) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="material-details-page">
      <SEO 
        title={material.title}
        description={`${material.title} - ${formatPrice(material.price)}. ${material.description?.substring(0, 100) || 'Materiał budowlany na sprzedaż.'}`}
        canonical={`/materials/${materialId}`}
        image={material.images?.[0] || undefined}
        type="article"
      />

      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-700 py-6">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/materials')} 
            className="text-emerald-100 hover:text-white hover:bg-emerald-500/20 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Powrót do listy
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Images */}
            <Card>
              <CardContent className="p-0">
                <div className="h-80 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden">
                  {material.images && material.images.length > 0 ? (
                    <img src={material.images[0]} alt={material.title} className="w-full h-full object-cover" />
                  ) : (
                    <Package className="w-24 h-24 text-slate-300" />
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Details */}
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-2xl">{material.title}</CardTitle>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant={material.condition === 'nowy' ? 'default' : 'secondary'}>
                        {material.condition === 'nowy' ? 'Nowy' : 'Używany'}
                      </Badge>
                      {material.quantity && (
                        <Badge variant="outline">
                          <Box className="w-3 h-3 mr-1" />
                          {material.quantity} {material.unit}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex flex-col items-end gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleShare}
                      data-testid="share-btn"
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4 mr-2 text-green-500" />
                          Skopiowano!
                        </>
                      ) : (
                        <>
                          <Share2 className="w-4 h-4 mr-2" />
                          Udostępnij
                        </>
                      )}
                    </Button>
                    <p className="text-3xl font-bold text-emerald-600">{formatPrice(material.price)}</p>
                    {material.price_negotiable && (
                      <p className="text-sm text-slate-500">do negocjacji</p>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <h3 className="font-semibold mb-2">Opis</h3>
                <p className="text-slate-700 whitespace-pre-wrap">{material.description || 'Brak opisu'}</p>
                
                <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t">
                  <div className="flex items-center gap-2 text-slate-600">
                    <MapPin className="w-4 h-4 text-emerald-500" />
                    <span>{material.location || 'Nie podano lokalizacji'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Tag className="w-4 h-4 text-emerald-500" />
                    <span>{material.category}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Seller Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  {material.seller_type === 'company' ? (
                    <Building2 className="w-5 h-5 text-emerald-500" />
                  ) : (
                    <User className="w-5 h-5 text-emerald-500" />
                  )}
                  Sprzedawca
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-semibold text-lg mb-4">{material.seller_name}</p>
                
                {material.contact_phone && (
                  <a href={`tel:${material.contact_phone}`} className="flex items-center gap-2 text-slate-600 hover:text-emerald-600 mb-2">
                    <Phone className="w-4 h-4" />
                    {material.contact_phone}
                  </a>
                )}
                
                {material.contact_email && (
                  <a href={`mailto:${material.contact_email}`} className="flex items-center gap-2 text-slate-600 hover:text-emerald-600">
                    <Mail className="w-4 h-4" />
                    {material.contact_email}
                  </a>
                )}
              </CardContent>
            </Card>

            {/* Contact Form */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-emerald-500" />
                  Wyślij wiadomość
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Napisz do sprzedawcy..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  className="mb-4"
                  data-testid="message-input"
                />
                <Button 
                  onClick={handleSendMessage} 
                  disabled={sending}
                  className="w-full bg-emerald-500 hover:bg-emerald-600"
                  data-testid="send-message-btn"
                >
                  {sending ? 'Wysyłanie...' : 'Wyślij wiadomość'}
                </Button>
                
                {!user && (
                  <p className="text-sm text-slate-500 text-center mt-2">
                    <a href="/login" className="text-emerald-600 hover:underline">Zaloguj się</a> aby wysłać wiadomość
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaterialDetails;
